// @effect-diagnostics globalFetch:off globalFetchInEffect:off -- The ticket exchange is one plain request on the client's own HTTP boundary.
import {
  ENVIRONMENT_WEBSOCKET_TICKET_PARAM,
  ENVIRONMENT_WEBSOCKET_TICKET_PATH,
  IssuedEnvironmentWebSocketTicket,
  type EnvironmentWebSocketTicket,
} from "@afar-research/contracts";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";

import {
  ConnectionBlockedError,
  ConnectionTransientError,
  type ConnectionAttemptError,
  type PrimaryConnectionTarget,
} from "./model.ts";
import { joinEnvironmentPath } from "../environment/endpoint.ts";

const decodeTicket = Schema.decodeUnknownEffect(IssuedEnvironmentWebSocketTicket);

/**
 * Exchanges a bearer session for a one-use WebSocket ticket on the Environment that issued it.
 *
 * A browser cannot attach a bearer header to a WebSocket upgrade, and a session in the socket URL
 * would outlive the connection it opened, so every bearer-holding client opens `/ws` with a ticket
 * minted just before. A refusal is a credential problem the person has to answer; anything else is
 * reachability and is retried.
 */
export const mintTicketOverHttp = Effect.fn("clientRuntime.connection.mintWebSocketTicket")(
  function* (input: {
    readonly httpBaseUrl: string;
    readonly bearer: string;
    readonly label: string;
  }) {
    const response = yield* Effect.tryPromise({
      try: () =>
        fetch(joinEnvironmentPath(input.httpBaseUrl, ENVIRONMENT_WEBSOCKET_TICKET_PATH), {
          method: "POST",
          headers: { authorization: `Bearer ${input.bearer}` },
          credentials: "omit",
        }),
      catch: () =>
        new ConnectionTransientError({
          reason: "network",
          detail: `${input.label} could not be reached to open a connection.`,
        }),
    });
    if (response.status === 401 || response.status === 403) {
      return yield* new ConnectionBlockedError({
        reason: "credential",
        detail: `${input.label} no longer accepts this client's access. Pair it again.`,
      });
    }
    if (!response.ok) {
      return yield* new ConnectionTransientError({
        reason: "endpoint-unavailable",
        detail: `${input.label} could not open a connection (${response.status}).`,
      });
    }
    const body = yield* Effect.tryPromise({
      try: () => response.json() as Promise<unknown>,
      catch: () =>
        new ConnectionTransientError({
          reason: "transport",
          detail: `${input.label} answered the connection request unreadably.`,
        }),
    });
    const issued = yield* decodeTicket(body).pipe(
      Effect.mapError(
        () =>
          new ConnectionTransientError({
            reason: "transport",
            detail: `${input.label} answered the connection request unreadably.`,
          }),
      ),
    );
    return issued.ticket;
  },
);

/**
 * How a client authorizes its WebSocket to an Environment.
 *
 * A browser served by the Environment itself carries its paired session as a cookie and needs
 * nothing here, which is the default. The Desktop Client holds a bearer session for its local
 * Environment and supplies `primaryBearer`; a Linked Computer's session comes from its SSH forward.
 * Either way, the bearer is exchanged for a ticket right before the socket opens.
 */
export class EnvironmentWebSocketAuthorization extends Context.Reference<{
  /** `refresh` asks for a new session after the Environment refused the last one. */
  readonly primaryBearer: (
    target: PrimaryConnectionTarget,
    options?: { readonly refresh?: boolean },
  ) => Effect.Effect<string | null, ConnectionAttemptError>;
  readonly mintTicket: (input: {
    readonly httpBaseUrl: string;
    readonly bearer: string;
    readonly label: string;
  }) => Effect.Effect<EnvironmentWebSocketTicket, ConnectionAttemptError>;
}>("@afar-research/client-runtime/connection/EnvironmentWebSocketAuthorization", {
  defaultValue: () => ({
    primaryBearer: () => Effect.succeed(null),
    mintTicket: mintTicketOverHttp,
  }),
}) {}

/** A socket URL for another route on the same Environment, carrying a ticket when there is one. */
export function environmentRouteSocketUrl(
  wsBaseUrl: string,
  path: string,
  ticket?: string,
): string {
  const url = joinEnvironmentPath(wsBaseUrl, path);
  url.search = "";
  if (ticket !== undefined) {
    url.searchParams.set(ENVIRONMENT_WEBSOCKET_TICKET_PARAM, ticket);
  }
  return url.toString();
}

/** The socket URL for a base URL, carrying a ticket when there is one. */
export function environmentSocketUrl(wsBaseUrl: string, ticket?: string): string {
  // The socket is `/ws` under the endpoint, whatever path the endpoint already has.
  const url = /\/ws\/?$/.test(new URL(wsBaseUrl).pathname)
    ? new URL(wsBaseUrl)
    : joinEnvironmentPath(wsBaseUrl, "/ws");
  if (ticket !== undefined) {
    url.searchParams.set(ENVIRONMENT_WEBSOCKET_TICKET_PARAM, ticket);
  }
  return url.toString();
}
